import { Floor, Element, WallElement, AreaElement, ShapeElement, DoorElement, StairElement, TextElement, Model3DElement } from '../../../types';

// 辅助函数：获取元素类型的中文标签（与RightPanel保持一致）
// 注意：这里的返回值是给 hiddenCategories 用的，保持中文标签
const getElementTypeLabel = (type: string): string => {
  const typeLabels: Record<string, string> = {
    wall: '墙体',
    door: '门',
    window: '窗',
    area: '区域',
    furniture: '家具',
    circle: '圆形',
    polygon: '多边形',
    stair: '楼梯',
    shape: '图形',
    text: '文本',
    model3d: '3D模型',
  };
  return typeLabels[type] || type;
};

/**
 * 检查元素是否在视口内（用于视口裁剪优化）
 */
function isElementInViewport(
  element: Element,
  viewportBounds: { minX: number; maxX: number; minY: number; maxY: number }
): boolean {
  if (element.points.length === 0) return false;
  
  // 计算元素边界框
  const minX = Math.min(...element.points.map(p => p.x));
  const maxX = Math.max(...element.points.map(p => p.x));
  const minY = Math.min(...element.points.map(p => p.y));
  const maxY = Math.max(...element.points.map(p => p.y));
  
  // 检查是否与视口相交
  return !(maxX < viewportBounds.minX || minX > viewportBounds.maxX ||
           maxY < viewportBounds.minY || minY > viewportBounds.maxY);
}

/**
 * 渲染所有元素
 */
export function renderElements(
  ctx: CanvasRenderingContext2D,
  floor: Floor,
  selectedElements: string[],
  hoveredElement: string | null,
  hoveredPoint?: { elementId: string; pointIndex: number } | null,
  hiddenCategories?: Record<string, boolean>,
  viewportBounds?: { minX: number; maxX: number; minY: number; maxY: number },
  zoom: number = 1
): void {
  // 固定像素大小，然后乘以 1/zoom 保持固定像素大小
  const scaleFactor = 1 / zoom;

  // 按图层顺序渲染
  for (const layer of floor.layers) {
    if (!layer.visible) continue;

    for (const element of layer.elements) {
      // 检查元素类别是否被隐藏
      const typeLabel = getElementTypeLabel(element.type);
      if (hiddenCategories && hiddenCategories[typeLabel]) {
        continue; // 跳过隐藏的类别
      }

      // 视口裁剪优化：跳过不在视口内的元素（除非被选中或悬停）
      const isSelected = selectedElements.includes(element.id);
      const isHovered = hoveredElement === element.id;
      
      if (viewportBounds && !isSelected && !isHovered) {
        if (!isElementInViewport(element, viewportBounds)) {
          continue; // 跳过不在视口内的元素
        }
      }

      const hoveredPtIdx = hoveredPoint?.elementId === element.id ? hoveredPoint.pointIndex : null;

      renderElement(ctx, element, isSelected, isHovered, hoveredPtIdx, scaleFactor);
    }
  }
}

/**
 * 渲染单个元素
 */
function renderElement(
  ctx: CanvasRenderingContext2D,
  element: Element,
  isSelected: boolean,
  isHovered: boolean,
  hoveredPointIndex: number | null,
  scaleFactor: number = 1
): void {
  ctx.save();

  switch (element.type) {
    case 'wall':
      renderWall(ctx, element as WallElement, isSelected, isHovered, hoveredPointIndex, scaleFactor);
      break;
    case 'area':
      renderArea(ctx, element as AreaElement, isSelected, isHovered, hoveredPointIndex, scaleFactor);
      break;
    case 'rectangle':
    case 'circle':
    case 'polygon':
      renderShape(ctx, element as ShapeElement, isSelected, isHovered, hoveredPointIndex, scaleFactor);
      break;
    case 'door':
      renderDoor(ctx, element as DoorElement, isSelected, isHovered, scaleFactor);
      break;
    case 'stair':
      renderStair(ctx, element as StairElement, isSelected, isHovered, scaleFactor);
      break;
    case 'model3d':
      renderModel3D(ctx, element as Model3DElement, isSelected, isHovered, hoveredPointIndex, scaleFactor);
      break;
    case 'text':
      renderText(ctx, element as TextElement, isSelected, isHovered, scaleFactor);
      break;
    default:
      // 默认渲染
      renderDefault(ctx, element, isSelected, isHovered, hoveredPointIndex, scaleFactor);
  }

  ctx.restore();
}

/**
 * 渲染墙面
 */
function renderWall(
  ctx: CanvasRenderingContext2D,
  wall: WallElement,
  isSelected: boolean,
  isHovered: boolean,
  hoveredPointIndex: number | null,
  scaleFactor: number = 1
): void {
  if (wall.points.length < 2) return;

  // mm转换为画布单位，然后乘以 scaleFactor 保持固定像素大小
  const baseThickness = wall.thickness / 50;
  const thickness = baseThickness * scaleFactor;
  const outlineWidth = thickness + 4 * scaleFactor;

  const opacity = wall.opacity ?? 1;

  // 先绘制浅色描边
  ctx.strokeStyle = '#9ca3af'; // 使用稍浅的描边
  ctx.lineWidth = outlineWidth;
  ctx.lineCap = 'butt'; // 直角端点
  ctx.lineJoin = 'miter';
  ctx.globalAlpha = 0.6 * opacity; // 应用透明度

  ctx.beginPath();
  ctx.moveTo(wall.points[0].x, wall.points[0].y);
  for (let i = 1; i < wall.points.length; i++) {
    ctx.lineTo(wall.points[i].x, wall.points[i].y);
  }
  ctx.stroke();

  // 绘制主线条
  ctx.strokeStyle = wall.color;
  ctx.lineWidth = thickness;
  ctx.globalAlpha = opacity;

  // 悬停效果
  if (isHovered) {
    ctx.strokeStyle = '#3b82f6';
  }

  // 选中效果
  if (isSelected) {
    ctx.strokeStyle = '#10b981';
  }

  ctx.beginPath();
  ctx.moveTo(wall.points[0].x, wall.points[0].y);
  for (let i = 1; i < wall.points.length; i++) {
    ctx.lineTo(wall.points[i].x, wall.points[i].y);
  }
  ctx.stroke();

  // 恢复完全不透明以绘制端点
  ctx.globalAlpha = 1;

  // 始终绘制端点（绿色）- 固定像素大小
  const baseOuterRadius = 5;
  const baseInnerRadius = 3.5;
  const outerRadius = baseOuterRadius * scaleFactor;
  const innerRadius = baseInnerRadius * scaleFactor;

  for (let i = 0; i < wall.points.length; i++) {
    const point = wall.points[i];
    const isHoveredPt = hoveredPointIndex === i;
    const currentOuterRadius = isHoveredPt ? outerRadius * 1.4 : outerRadius;
    const currentInnerRadius = isHoveredPt ? innerRadius * 1.4 : innerRadius;

    // 绘制外圈白色描边
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(point.x, point.y, currentOuterRadius, 0, Math.PI * 2);
    ctx.fill();

    // 绘制主色（绿色）
    ctx.fillStyle = '#10b981';
    ctx.beginPath();
    ctx.arc(point.x, point.y, currentInnerRadius, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * 渲染区域
 */
function renderArea(
  ctx: CanvasRenderingContext2D,
  area: AreaElement,
  isSelected: boolean,
  isHovered: boolean,
  hoveredPointIndex: number | null,
  scaleFactor: number = 1
): void {
  if (area.points.length < 3) return;

  // 固定线宽
  const baseLineWidth = isSelected ? 3 : isHovered ? 2 : 1;
  const lineWidth = baseLineWidth * scaleFactor;

  // 填充
  ctx.fillStyle = area.color;
  ctx.globalAlpha = area.fillOpacity;
  ctx.beginPath();
  ctx.moveTo(area.points[0].x, area.points[0].y);
  for (let i = 1; i < area.points.length; i++) {
    ctx.lineTo(area.points[i].x, area.points[i].y);
  }
  ctx.closePath();
  ctx.fill();

  ctx.globalAlpha = 1;

  // 边框
  ctx.strokeStyle = area.color;
  ctx.lineWidth = lineWidth;
  ctx.stroke();

  // 显示区域名称
  if (area.areaName && area.showName !== false) {
    // 计算区域中心点
    let centerX = 0;
    let centerY = 0;
    for (const point of area.points) {
      centerX += point.x;
      centerY += point.y;
    }
    centerX /= area.points.length;
    centerY /= area.points.length;

    // 绘制背景
    ctx.font = '14px Arial';
    const textMetrics = ctx.measureText(area.areaName);
    const padding = 6;
    const bgWidth = textMetrics.width + padding * 2;
    const bgHeight = 20;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(
      centerX - bgWidth / 2,
      centerY - bgHeight / 2,
      bgWidth,
      bgHeight
    );

    // 绘制边框
    ctx.strokeStyle = area.color;
    ctx.lineWidth = 1;
    ctx.strokeRect(
      centerX - bgWidth / 2,
      centerY - bgHeight / 2,
      bgWidth,
      bgHeight
    );

    // 绘制文字
    ctx.fillStyle = '#1f2937';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(area.areaName, centerX, centerY);
  }

  // 端点 - 固定像素大小
  if (isSelected || isHovered) {
    const baseRadius = isSelected ? 5 : 5;
    const radius = baseRadius * scaleFactor;
    ctx.fillStyle = isSelected ? '#10b981' : '#3b82f6';
    for (let i = 0; i < area.points.length; i++) {
      const point = area.points[i];
      const isHoveredPt = hoveredPointIndex === i;
      const currentRadius = isHoveredPt ? radius * 1.4 : radius;

      ctx.beginPath();
      ctx.arc(point.x, point.y, currentRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * 渲染形状
 */
function renderShape(
  ctx: CanvasRenderingContext2D,
  shape: ShapeElement,
  isSelected: boolean,
  isHovered: boolean,
  hoveredPointIndex: number | null,
  scaleFactor: number = 1
): void {
  if (shape.points.length < 2) return;

  // 固定线宽
  const baseLineWidth = isSelected ? 3 : isHovered ? 2 : 1;
  const lineWidth = baseLineWidth * scaleFactor;

  ctx.fillStyle = shape.color;
  ctx.strokeStyle = shape.color;
  ctx.lineWidth = lineWidth;

  if (shape.type === 'rectangle') {
    // 矩形 - 使用4个角点计算边界
    const minX = Math.min(...shape.points.map(p => p.x));
    const maxX = Math.max(...shape.points.map(p => p.x));
    const minY = Math.min(...shape.points.map(p => p.y));
    const maxY = Math.max(...shape.points.map(p => p.y));
    const width = maxX - minX;
    const height = maxY - minY;

    ctx.globalAlpha = 0.3;
    ctx.fillRect(minX, minY, width, height);
    ctx.globalAlpha = 1;
    ctx.strokeRect(minX, minY, width, height);

    // 显示家具名称
    if (shape.furnitureName) {
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      // 绘制背景
      ctx.font = '12px Arial';
      const textMetrics = ctx.measureText(shape.furnitureName);
      const padding = 4;
      const bgWidth = textMetrics.width + padding * 2;
      const bgHeight = 16;

      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(
        centerX - bgWidth / 2,
        centerY - bgHeight / 2,
        bgWidth,
        bgHeight
      );

      // 绘制文字
      ctx.fillStyle = shape.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(shape.furnitureName, centerX, centerY);
    }
  } else if (shape.type === 'circle') {
    // 圆形
    const center = shape.points[0];
    const edge = shape.points[1];
    const radius = Math.sqrt(
      Math.pow(edge.x - center.x, 2) + Math.pow(edge.y - center.y, 2)
    );

    ctx.beginPath();
    ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    ctx.globalAlpha = 0.3;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.stroke();

    // 显示圆形设备名称
    if (shape.furnitureName) {
      // 绘制背景
      ctx.font = '12px Arial';
      const textMetrics = ctx.measureText(shape.furnitureName);
      const padding = 4;
      const bgWidth = textMetrics.width + padding * 2;
      const bgHeight = 16;

      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(
        center.x - bgWidth / 2,
        center.y - bgHeight / 2,
        bgWidth,
        bgHeight
      );

      // 绘制文字
      ctx.fillStyle = shape.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(shape.furnitureName, center.x, center.y);
    }
  } else if (shape.type === 'polygon') {
    // 多边形
    ctx.beginPath();
    ctx.moveTo(shape.points[0].x, shape.points[0].y);
    for (let i = 1; i < shape.points.length; i++) {
      ctx.lineTo(shape.points[i].x, shape.points[i].y);
    }
    ctx.closePath();
    ctx.globalAlpha = 0.3;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.stroke();

    // 显示多边形设备名称
    if (shape.furnitureName) {
      // 计算多边形中心点
      let centerX = 0;
      let centerY = 0;
      for (const point of shape.points) {
        centerX += point.x;
        centerY += point.y;
      }
      centerX /= shape.points.length;
      centerY /= shape.points.length;

      // 绘制背景
      ctx.font = '12px Arial';
      const textMetrics = ctx.measureText(shape.furnitureName);
      const padding = 4;
      const bgWidth = textMetrics.width + padding * 2;
      const bgHeight = 16;

      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(
        centerX - bgWidth / 2,
        centerY - bgHeight / 2,
        bgWidth,
        bgHeight
      );

      // 绘制文字
      ctx.fillStyle = shape.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(shape.furnitureName, centerX, centerY);
    }
  }

  // 端点 - 固定像素大小
  if (isSelected || isHovered) {
    const baseRadius = 5;
    const radius = baseRadius * scaleFactor;
    ctx.fillStyle = isSelected ? '#10b981' : '#3b82f6';
    for (let i = 0; i < shape.points.length; i++) {
      const point = shape.points[i];
      const isHoveredPt = hoveredPointIndex === i;
      const currentRadius = isHoveredPt ? radius * 1.4 : radius;

      ctx.beginPath();
      ctx.arc(point.x, point.y, currentRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * 默认渲染
 */
function renderDefault(
  ctx: CanvasRenderingContext2D,
  element: Element,
  isSelected: boolean,
  isHovered: boolean,
  hoveredPointIndex: number | null,
  scaleFactor: number = 1
): void {
  if (element.points.length < 2) return;

  // 固定线宽
  const lineWidth = 2 * scaleFactor;

  ctx.strokeStyle = isSelected ? '#10b981' : isHovered ? '#3b82f6' : '#666666';
  ctx.lineWidth = lineWidth;
  ctx.setLineDash([5 * scaleFactor, 5 * scaleFactor]);

  ctx.beginPath();
  ctx.moveTo(element.points[0].x, element.points[0].y);
  for (let i = 1; i < element.points.length; i++) {
    ctx.lineTo(element.points[i].x, element.points[i].y);
  }
  ctx.stroke();

  ctx.setLineDash([]);

  // 端点 - 固定像素大小
  const baseRadius = 4;
  const radius = baseRadius * scaleFactor;
  ctx.fillStyle = isSelected ? '#10b981' : isHovered ? '#3b82f6' : '#666666';
  for (let i = 0; i < element.points.length; i++) {
    const point = element.points[i];
    const isHoveredPt = hoveredPointIndex === i;
    const currentRadius = isHoveredPt ? radius * 1.4 : radius;

    ctx.beginPath();
    ctx.arc(point.x, point.y, currentRadius, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * 渲染门
 */
function renderDoor(
  ctx: CanvasRenderingContext2D,
  door: DoorElement,
  isSelected: boolean,
  isHovered: boolean,
  scaleFactor: number = 1
): void {
  if (door.points.length < 2) return;

  const p1 = door.points[0];
  const p2 = door.points[1];
  const centerX = (p1.x + p2.x) / 2;
  const centerY = (p1.y + p2.y) / 2;

  // 计算门的宽度（两个点之间的距离）
  const doorWidth = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);

  // 固定线宽
  const baseLineWidth = isSelected ? 4 : 3;
  const lineWidth = baseLineWidth * scaleFactor;
  const dashLineWidth = 1 * scaleFactor;

  // 绘制门的位置标记（短线段）
  ctx.strokeStyle = isSelected ? '#10b981' : isHovered ? '#3b82f6' : '#8b5cf6';
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.moveTo(p1.x, p1.y);
  ctx.lineTo(p2.x, p2.y);
  ctx.stroke();

  // 绘制门开启弧线
  const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
  const openAngle = (door.openAngle || 90) * Math.PI / 180;

  ctx.strokeStyle = isSelected ? '#10b981' : isHovered ? '#3b82f6' : '#8b5cf6';
  ctx.lineWidth = dashLineWidth;
  ctx.setLineDash([3 * scaleFactor, 3 * scaleFactor]);
  ctx.beginPath();
  ctx.arc(p1.x, p1.y, doorWidth, angle, angle + openAngle);
  ctx.stroke();
  ctx.setLineDash([]);
  
  // 绘制门标识文字
  ctx.font = '10px Arial';
  ctx.fillStyle = '#8b5cf6';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('门', centerX, centerY);
}

/**
 * 渲染窗
 * @deprecated 暂未使用，已注释
 */
/*
function renderWindow(
  ctx: CanvasRenderingContext2D,
  window: any,
  isSelected: boolean,
  isHovered: boolean
): void {
  if (window.points.length < 2) return;

  const p1 = window.points[0];
  const p2 = window.points[1];
  const centerX = (p1.x + p2.x) / 2;
  const centerY = (p1.y + p2.y) / 2;
  
  // 绘制窗的位置标记（短线段，双线表示）
  ctx.strokeStyle = isSelected ? '#10b981' : isHovered ? '#3b82f6' : '#0ea5e9';
  ctx.lineWidth = isSelected ? 3 : 2;
  
  // 计算垂直方向
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  const perpX = -dy / len * 3;
  const perpY = dx / len * 3;
  
  // 绘制双线
  ctx.beginPath();
  ctx.moveTo(p1.x + perpX, p1.y + perpY);
  ctx.lineTo(p2.x + perpX, p2.y + perpY);
  ctx.stroke();
  
  ctx.beginPath();
  ctx.moveTo(p1.x - perpX, p1.y - perpY);
  ctx.lineTo(p2.x - perpX, p2.y - perpY);
  ctx.stroke();
  
  // 绘制窗框中线
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  ctx.beginPath();
  ctx.moveTo(centerX + perpX * 2, centerY + perpY * 2);
  ctx.lineTo(centerX - perpX * 2, centerY - perpY * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  
  // 绘制窗标识文字
  ctx.font = '10px Arial';
  ctx.fillStyle = '#0ea5e9';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('窗', centerX, centerY);
}
*/

/**
 * 渲染楼梯
 */
function renderStair(
  ctx: CanvasRenderingContext2D,
  stair: StairElement,
  isSelected: boolean,
  isHovered: boolean,
  scaleFactor: number = 1
): void {
  if (stair.points.length < 2) return;

  const p1 = stair.points[0];
  const p2 = stair.points[1];
  const width = Math.abs(p2.x - p1.x);
  const height = Math.abs(p2.y - p1.y);

  // 固定线宽
  const baseLineWidth = isSelected ? 3 : 2;
  const lineWidth = baseLineWidth * scaleFactor;

  // 绘制矩形框表示楼梯的位置
  ctx.fillStyle = isSelected ? 'rgba(16, 185, 129, 0.2)' : isHovered ? 'rgba(59, 130, 246, 0.2)' : 'rgba(234, 88, 12, 0.2)';
  ctx.fillRect(p1.x, p1.y, width, height);

  ctx.strokeStyle = isSelected ? '#10b981' : isHovered ? '#3b82f6' : '#ea580c';
  ctx.lineWidth = lineWidth;
  ctx.strokeRect(p1.x, p1.y, width, height);
  
  // 绘制阶梯线条表示楼梯
  const steps = Math.min(stair.steps || 10, 15);
  const stepHeight = height / steps;
  
  ctx.beginPath();
  for (let i = 1; i < steps; i++) {
    const y = p1.y + i * stepHeight;
    ctx.moveTo(p1.x, y);
    ctx.lineTo(p2.x, y);
  }
  ctx.stroke();
  
  const centerX = (p1.x + p2.x) / 2;
  const centerY = (p1.y + p2.y) / 2;
  
  ctx.font = '12px Arial';
  ctx.fillStyle = '#ea580c';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(stair.direction === 'up' ? '↑楼梯' : '↓楼梯', centerX, centerY);
}

/**
 * 渲染3D模型元素 - 简洁线稿风格（无外框，直接缩放轮廓）
 */
function renderModel3D(
  ctx: CanvasRenderingContext2D,
  model: Model3DElement,
  isSelected: boolean,
  isHovered: boolean,
  hoveredPointIndex: number | null,
  scaleFactor: number = 1
): void {
  if (model.points.length < 4) return;

  // model.points 已经是旋转后的四个角点坐标
  // 计算中心点和未旋转的宽高
  const centerX = model.points.reduce((sum, p) => sum + p.x, 0) / model.points.length;
  const centerY = model.points.reduce((sum, p) => sum + p.y, 0) / model.points.length;

  // 使用 model.width 和 model.depth 作为未旋转的尺寸
  const unrotatedWidth = model.width;
  const unrotatedDepth = model.depth;

  // 应用旋转变换（绘制简笔画和控制点）
  ctx.save();
  ctx.translate(centerX, centerY);
  if (model.rotation) {
    ctx.rotate((model.rotation * Math.PI) / 180);
  }

  // 线稿颜色 - 使用元素的自定义颜色
  const baseColor = model.color || '#64748b';
  const strokeColor = isSelected ? '#10b981' : isHovered ? '#3b82f6' : baseColor;

  // 固定线宽
  const baseLineWidth = isSelected ? 1.5 : 1.2;
  const lineWidth = baseLineWidth * scaleFactor;

  // 绘制模型轮廓线稿（直接缩放到当前尺寸）
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // 如果有轮廓数据，绘制实际轮廓并缩放到当前尺寸
  if (model.outlineData && model.outlineData.lines.length > 0) {
    const outline = model.outlineData;
    
    // 使用 unrotatedWidth 和 unrotatedDepth 计算缩放比例
    const scaleX = unrotatedWidth / outline.width;
    const scaleY = unrotatedDepth / outline.height;
    
    // 性能优化：根据线段数量决定渲染策略
    const lineCount = outline.lines.length;
    
    if (lineCount > 150 && !isSelected && !isHovered) {
      // 超过150条线段且未选中/悬停：只绘制简化的矩形边框
      const halfWidth = unrotatedWidth / 2;
      const halfDepth = unrotatedDepth / 2;
      ctx.strokeRect(-halfWidth, -halfDepth, unrotatedWidth, unrotatedDepth);
      
      // 添加对角线表示这是一个3D模型
      ctx.setLineDash([4, 4]);
      ctx.globalAlpha = 0.4;
      ctx.beginPath();
      ctx.moveTo(-halfWidth * 0.6, -halfDepth * 0.6);
      ctx.lineTo(halfWidth * 0.6, halfDepth * 0.6);
      ctx.moveTo(halfWidth * 0.6, -halfDepth * 0.6);
      ctx.lineTo(-halfWidth * 0.6, halfDepth * 0.6);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    } else {
      // 使用Path2D一次性绘制所有线段（性能优化）
      const path = new Path2D();
      
      // 如果线段太多，进行采样（每隔N条绘制一条）
      const step = lineCount > 100 ? 2 : 1;
      
      for (let i = 0; i < outline.lines.length; i += step) {
        const line = outline.lines[i];
        if (line.length < 2) continue;
        
        // 将轮廓坐标从左上角原点转换为相对于中心点（已经在旋转变换中）
        const localX1 = (line[0].x - outline.width / 2) * scaleX;
        const localY1 = (line[0].y - outline.height / 2) * scaleY;
        const localX2 = (line[1].x - outline.width / 2) * scaleX;
        const localY2 = (line[1].y - outline.height / 2) * scaleY;
        
        path.moveTo(localX1, localY1);
        path.lineTo(localX2, localY2);
      }
      
      // 一次性绘制所有线段
      ctx.stroke(path);
    }
  } else {
    // 没有轮廓数据，绘制简单的3D图标（相对于中心点）
    const halfWidth = unrotatedWidth / 2;
    const halfDepth = unrotatedDepth / 2;
    const depth3D = Math.min(unrotatedWidth, unrotatedDepth) * 0.15;
    
    // 底面矩形
    ctx.beginPath();
    ctx.rect(-halfWidth, -halfDepth + depth3D, unrotatedWidth, unrotatedDepth - depth3D);
    ctx.stroke();

    // 顶面（3D效果）
    ctx.beginPath();
    ctx.moveTo(-halfWidth, -halfDepth + depth3D);
    ctx.lineTo(-halfWidth + depth3D, -halfDepth);
    ctx.lineTo(halfWidth + depth3D * 0.5, -halfDepth);
    ctx.lineTo(halfWidth, -halfDepth + depth3D);
    ctx.closePath();
    ctx.stroke();

    // 右侧面（3D效果）
    ctx.beginPath();
    ctx.moveTo(halfWidth, -halfDepth + depth3D);
    ctx.lineTo(halfWidth + depth3D * 0.5, -halfDepth);
    ctx.lineTo(halfWidth + depth3D * 0.5, halfDepth - depth3D * 0.5);
    ctx.lineTo(halfWidth, halfDepth);
    ctx.stroke();

    // 内部装饰线（表示这是一个3D物体）
    ctx.setLineDash([3, 3]);
    ctx.globalAlpha = 0.5;
    
    // 对角线
    ctx.beginPath();
    ctx.moveTo(-halfWidth * 0.6, -halfDepth * 0.6 + depth3D);
    ctx.lineTo(halfWidth * 0.6, halfDepth * 0.6);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(halfWidth * 0.6, -halfDepth * 0.6 + depth3D);
    ctx.lineTo(-halfWidth * 0.6, halfDepth * 0.6);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }

  // 选中时显示控制点（四个角）- 在旋转变换内绘制（相对于中心点）
  if (isSelected || isHovered) {
    const baseCornerSize = 6;
    const cornerSize = baseCornerSize * scaleFactor;
    const halfWidth = unrotatedWidth / 2;
    const halfDepth = unrotatedDepth / 2;
    const handleLineWidth = 1 * scaleFactor;
    const controlLineWidth = 1.5 * scaleFactor;

    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = controlLineWidth;

    // 四个角的控制点（相对于中心点的未旋转位置）
    const corners = [
      { x: -halfWidth, y: -halfDepth },  // 左上
      { x: halfWidth, y: -halfDepth },   // 右上
      { x: halfWidth, y: halfDepth },    // 右下
      { x: -halfWidth, y: halfDepth },   // 左下
    ];

    corners.forEach((corner, i) => {
      const isHoveredPt = hoveredPointIndex === i;
      const size = isHoveredPt ? cornerSize * 1.4 : cornerSize;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(corner.x - size / 2, corner.y - size / 2, size, size);
      ctx.strokeRect(corner.x - size / 2, corner.y - size / 2, size, size);
    });

    // 选中时显示旋转手柄（顶部中间）
    if (isSelected) {
      const baseHandleY = -halfDepth - 25;
      const handleY = baseHandleY;

      // 连接线
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = handleLineWidth;
      ctx.setLineDash([2 * scaleFactor, 2 * scaleFactor]);
      ctx.beginPath();
      ctx.moveTo(0, -halfDepth);
      ctx.lineTo(0, handleY + 8 * scaleFactor);
      ctx.stroke();
      ctx.setLineDash([]);

      // 旋转手柄圆形
      const baseHandleRadius = 8;
      const handleRadius = baseHandleRadius * scaleFactor;
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = controlLineWidth;
      ctx.beginPath();
      ctx.arc(0, handleY, handleRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // 旋转图标
      const baseIconRadius = 4;
      const iconRadius = baseIconRadius * scaleFactor;
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = controlLineWidth;
      ctx.beginPath();
      ctx.arc(0, handleY, iconRadius, -Math.PI * 0.8, Math.PI * 0.3);
      ctx.stroke();

      // 箭头
      const arrowAngle = Math.PI * 0.3;
      const arrowX = iconRadius * Math.cos(arrowAngle);
      const arrowY = handleY + iconRadius * Math.sin(arrowAngle);
      ctx.beginPath();
      ctx.moveTo(arrowX - 2 * scaleFactor, arrowY - 3 * scaleFactor);
      ctx.lineTo(arrowX, arrowY);
      ctx.lineTo(arrowX + 3 * scaleFactor, arrowY - 2 * scaleFactor);
      ctx.stroke();
    }
  }

  ctx.restore();

  // 显示模型名称（在底部外侧，不旋转）
  ctx.font = '11px Arial';
  ctx.fillStyle = strokeColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  
  // 计算旋转后底部边缘的Y坐标
  const halfDepth = unrotatedDepth / 2;
  const rotationRad = (model.rotation || 0) * Math.PI / 180;
  const bottomY = centerY + halfDepth * Math.abs(Math.cos(rotationRad)) + (unrotatedWidth / 2) * Math.abs(Math.sin(rotationRad));
  
  ctx.fillText(model.modelName, centerX, bottomY + 6);
}


/**
 * 渲染文本元素
 */
function renderText(
  ctx: CanvasRenderingContext2D,
  text: TextElement,
  isSelected: boolean,
  isHovered: boolean,
  scaleFactor: number = 1
): void {
  if (text.points.length === 0) return;

  const [point] = text.points;
  const {
    content,
    fontSize = 16,
    color = '#000000',
    fontFamily = 'Arial, sans-serif',
    bold = false,
    italic = false,
    align = 'center',
    borderColor = '#ADD8E6',
    backgroundColor = '#FFFFFF',
    backgroundOpacity = 0.8,
    padding = 8,
  } = text;

  // 应用缩放因子，保持固定像素大小
  const actualFontSize = fontSize * scaleFactor;
  const actualPadding = padding * scaleFactor;
  const actualBorderWidth = 2 * scaleFactor;

  // 设置字体样式
  ctx.font = `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${actualFontSize}px ${fontFamily}`;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';

  // 测量文本尺寸
  const metrics = ctx.measureText(content);
  const textWidth = metrics.width;
  const textHeight = actualFontSize * 1.2; // 近似高度

  // 计算背景矩形位置（根据对齐方式）
  let bgX = point.x - actualPadding;
  let bgY = point.y - textHeight / 2 - actualPadding;
  let bgWidth = textWidth + actualPadding * 2;
  let bgHeight = textHeight + actualPadding * 2;

  if (align === 'center') {
    bgX = point.x - textWidth / 2 - actualPadding;
  } else if (align === 'right') {
    bgX = point.x - textWidth - actualPadding;
  }

  // 绘制背景
  ctx.fillStyle = backgroundColor;
  ctx.globalAlpha = backgroundOpacity;
  ctx.fillRect(bgX, bgY, bgWidth, bgHeight);

  // 绘制边框
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = actualBorderWidth;
  ctx.globalAlpha = 1;
  ctx.strokeRect(bgX, bgY, bgWidth, bgHeight);

  // 如果被选中或悬停，添加高亮边框
  if (isSelected || isHovered) {
    ctx.strokeStyle = isSelected ? '#3b82f6' : '#60a5fa';
    ctx.lineWidth = actualBorderWidth + scaleFactor;
    ctx.strokeRect(bgX - 2 * scaleFactor, bgY - 2 * scaleFactor, bgWidth + 4 * scaleFactor, bgHeight + 4 * scaleFactor);
  }

  // 绘制文本
  ctx.fillStyle = color;
  ctx.fillText(content, point.x, point.y);

  // 如果被选中，绘制控制点
  if (isSelected) {
    ctx.fillStyle = '#3b82f6';
    ctx.beginPath();
    ctx.arc(point.x, point.y, 4 * scaleFactor, 0, Math.PI * 2);
    ctx.fill();
  }
}
